import { useMutation } from "@apollo/client";
import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";

import { Error } from "./Error";

import { useQuery } from "@otta/search/apollo";
import { Spinner } from "@otta/shared-components";
import { pushAnalyticsEvent } from "@otta/analytics";
import { Button, Heading, Spacing } from "@otta/design";
import { CurrentUserDocument, VerifyEmailDocument } from "@otta/search/schema";

export function Verify({ token, mode }: { token: string; mode?: string }) {
  const navigate = useNavigate();

  const [verify, { loading, error, called }] = useMutation(
    VerifyEmailDocument,
    {
      refetchQueries: [{ query: CurrentUserDocument }],
      onCompleted: () => {
        pushAnalyticsEvent({ eventName: "Candidate Verified Email" });
      },
    }
  );

  const { data } = useQuery(CurrentUserDocument);
  const loggedIn = !!data?.currentUser;

  const navigateToJobs = () => {
    const firstVisit = localStorage.getItem("first_visit");
    navigate("/jobs", { replace: true, state: { onboard: firstVisit } });
  };

  useEffect(() => {
    if (mode === "jwt") {
      verify({ context: { emailToken: token } });
    } else {
      verify({ variables: { token } });
    }
  }, [token, verify, mode]);

  if (
    error?.message ===
    "This email verification token has expired. Request another."
  ) {
    return (
      <Error
        title="This link has expired"
        body="Email verification links expire after 2 hours and can only be used once."
        action={() => {
          navigate(".", { replace: true });
        }}
      />
    );
  }

  if (error) {
    return <Error action={() => verify({ variables: { token } })} />;
  }

  if (!called || loading) {
    return <Spinner />;
  }

  return (
    <div style={{ textAlign: "center", padding: "2rem" }}>
      <Spacing>
        <Heading bold size={3} align="center">
          Email verified
        </Heading>

        {loggedIn ? (
          <Button onClick={navigateToJobs} level="primary">
            View your jobs
          </Button>
        ) : (
          <Button as={Link} to="/login" level="primary">
            Click to login
          </Button>
        )}
      </Spacing>
    </div>
  );
}
